import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, useMediaQuery } from '@mui/material';
import { Check, UserCircle } from '@phosphor-icons/react';
import { useDispatch } from 'react-redux';

import { ProviderDoctorIcon } from 'enrollment/assets/ProviderDoctorIcon';
import { getDependentAge } from 'enrollment/features/Shared/FamilySelect/utils';
import { PsButtonTuner } from 'stencilSetup';
import { displayDependentAvatar } from 'enrollment/features/Family/FamilyDetailsPage/components';
import {
  ProviderWrapperStyled,
  ProviderTableStyled,
  ProviderBoxStyled,
  AddedProvidersBoxStyled,
  ProviderTableRowStyled,
  ProviderNameStyled,
  ProviderTextWrappperStyled,
  NameColumnStyled,
  ProviderInfoColumnStyled,
  ProviderCodeStyled,
  ButtonWrapperStyled,
  NameStyled,
} from '../../PlanDetailsStyled';
import { PrimaryCarePlanDetailsProps } from '../../types';
import { DependentDto } from 'theSource/features/FamilyMembers/types';
import { validatePcpDependents, validatePcpEmployee } from '../../utils';
import { api } from 'theSource/utils';
import { useConfigurationQuery } from 'theSource/queries';
import {
  setHideSubscriberCheckboxWhenChecked,
  setOpenChoosePCPDrawer,
  setSpecialityDropdownOptions,
  setSubscriberCheckbox,
} from '../ChoosePCPDrawer/SearchResultsPCPDrawer/services';
import { ChoosePCPDrawer } from '../ChoosePCPDrawer/ChoosePCPDrawer';

export const PrimaryCarePlanDetails = ({
  subscriber,
  dependents,
  selectedDependentIds,
  pcpMembers,
  selected,
  benefitLookup,
  electPlan,
  setPcpElectionData,
  setAllowPcpElection,
  togglePcpDrawer,
  openChooseProviderDrawer,
  setOpenChooseProviderDrawer,
  setShowPcpDrawer,
  isPcpOptional,
  plan,
  isFindAProviderEnabled,
  hideDependentCheckboxWhenChecked,
  setHideDependentCheckboxWhenChecked,
  isAddProvider = false,
  subscribeZipCode,
  setPcpDrawerRequired,
  isEsignPlanRequired,
}: PrimaryCarePlanDetailsProps) => {
  const isMobile = useMediaQuery('(max-width: 743px)');
  const { t } = useTranslation('enrollment.benefits');
  const providerDetailsTable = useRef<HTMLElement>(null);
  const [showMoreProviders, setShowMoreProviders] = useState(false);

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;

  const dispatch = useDispatch();

  const handleProviderChange = () => {
    dispatch(setOpenChoosePCPDrawer(true));

    setShowPcpDrawer(false);

    api
      .get(`${lumosApiHost}/v1/self_service/primary_care_providers/search`, {
        org_plan_id: plan?.plan_id,
        zip_code: subscribeZipCode,
        distance: 10,
      })
      .then((response: any) => {
        dispatch(setSpecialityDropdownOptions(response?.filters));
      })
      .catch((err) => {
        console.log(err?.response?.data?.details);
      });
  };

  const handleMoreProvidersClick = () => {
    if (isMobile) {
      if (benefitLookup === 'dental' && isFindAProviderEnabled) {
        handleProviderChange();
      } else {
        setAllowPcpElection(true);
        togglePcpDrawer();
      }
    } else {
      setShowMoreProviders(!showMoreProviders);
    }
  };

  useEffect(() => {
    if (showMoreProviders) {
      //Hide show more providers if screen is resized to mobile
      if (isMobile && benefitLookup !== 'dental') {
        setShowMoreProviders(false);
      }

      const parentCard = window.document.getElementById(`plan-card-${plan?.plan_id}`);
      if (parentCard) {
        parentCard.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  }, [isMobile, showMoreProviders, plan, benefitLookup]);

  const selectedDependents = dependents?.data.dependents.filter((dependent) =>
    selectedDependentIds?.includes(dependent.id)
  ) as DependentDto[] | undefined;

  const displayDependentProvider = () => {
    return selectedDependents?.map((dependent: DependentDto) => {
      const dependentPcpData = pcpMembers?.dependents.find(
        (depPcpData) => depPcpData.id === dependent.id
      );

      return (
        <ProviderTableRowStyled
          data-testid="dependents-provider-info"
          key={dependent.id}
        >
          <NameColumnStyled>
            <NameStyled>
              {displayDependentAvatar(dependent?.dependent_profile, 24)}
              <Typography
                variant="body1"
                className="ml-2 truncate text-capitalize"
              >
                {dependent?.first_name}{' '}
              </Typography>
              <Typography variant="body1" className="pl-2">
                {dependent?.birthdate
                  ? ` (${getDependentAge(dependent.birthdate)})`
                  : ''}
              </Typography>
            </NameStyled>
          </NameColumnStyled>

          <ProviderInfoColumnStyled>
            {dependentPcpData?.pcp_name && dependentPcpData?.pcp_name !== '' ? (
              <ProviderNameStyled>
                <Typography
                  variant="overline"
                  sx={{ marginRight: '1rem' }}
                  dangerouslySetInnerHTML={{
                    __html: ProviderDoctorIcon,
                  }}
                  data-testid="dependent-doctor-icon"
                />
                <Typography
                  variant="body1 text-capitalize"
                  fontWeight={600}
                  fontSize={16}
                  style={{ maxWidth: '15ch', width: 'auto' }}
                >
                  {dependentPcpData.pcp_name}
                </Typography>
              </ProviderNameStyled>
            ) : null}

            <ProviderCodeStyled>
              <Typography
                variant="body1"
                className="truncate"
                fontWeight={400}
                fontSize={16}
              >
                {dependentPcpData?.pcp_code}
              </Typography>
            </ProviderCodeStyled>
            <ButtonWrapperStyled>
              <PsButtonTuner
                buttonId={`change-provider-${dependentPcpData?.id}`}
                label={t('common:change')}
                level="tertiary"
                className="change-provider-button"
                onClick={() => {
                  if (benefitLookup === 'dental' && isFindAProviderEnabled) {
                    const id = `dependent-provider-${dependentPcpData?.id}`;
                    dispatch(setHideSubscriberCheckboxWhenChecked(false));
                    setHideDependentCheckboxWhenChecked({ [id]: false });

                    dispatch(setOpenChoosePCPDrawer(true));
                    handleProviderChange();
                  } else if (
                    (benefitLookup === 'dental' && !isFindAProviderEnabled) ||
                    benefitLookup === 'medical'
                  ) {
                    setAllowPcpElection(true);
                    togglePcpDrawer();
                  }
                }}
              />
            </ButtonWrapperStyled>
          </ProviderInfoColumnStyled>
        </ProviderTableRowStyled>
      );
    });
  };

  const displaySubscriberProvider = () => {
    return (
      <ProviderTableRowStyled>
        <NameColumnStyled>
          <div data-testid="you" className="covered pr-2">
            {subscriber ? (
              displayDependentAvatar(subscriber, 24)
            ) : (
              <UserCircle size={24} alt="You are covered" />
            )}
            <Typography variant="body1" className="ml-2">
              You
            </Typography>
          </div>
        </NameColumnStyled>

        <ProviderInfoColumnStyled>
          {pcpMembers?.pcp_name && pcpMembers?.pcp_name !== '' ? (
            <ProviderNameStyled>
              <Typography
                variant="overline"
                sx={{ marginRight: '1rem' }}
                dangerouslySetInnerHTML={{
                  __html: ProviderDoctorIcon,
                }}
                data-testid="doctor-icon"
              />
              <Typography
                variant="body1 text-capitalize truncate"
                fontWeight={600}
                fontSize={16}
                style={{ maxWidth: '15ch', width: 'auto' }}
              >
                {pcpMembers?.pcp_name}
              </Typography>
            </ProviderNameStyled>
          ) : null}

          <ProviderCodeStyled>
            <Typography
              variant="body1"
              className="truncate"
              fontWeight={400}
              fontSize={16}
            >
              {pcpMembers?.pcp_code}
            </Typography>
          </ProviderCodeStyled>
          <ButtonWrapperStyled>
            <PsButtonTuner
              buttonId={`change-provider-${subscriber?.id}`}
              label="Change"
              level="tertiary"
              className="change-provider-button"
              onClick={() => {
                if (benefitLookup === 'dental' && isFindAProviderEnabled) {
                  dispatch(setHideSubscriberCheckboxWhenChecked(true));
                  dispatch(setSubscriberCheckbox(false));
                  selectedDependents?.forEach((dependent: any) => {
                    const dependentId = `dependent-provider-${dependent?.id}`;
                    setHideDependentCheckboxWhenChecked((prevState: any) => {
                      const newState = { ...prevState };
                      newState[dependentId] = true;

                      return newState;
                    });
                  });
                  handleProviderChange();
                } else if (
                  (benefitLookup === 'dental' && !isFindAProviderEnabled) ||
                  benefitLookup === 'medical'
                ) {
                  setAllowPcpElection(true);
                  togglePcpDrawer();
                }
              }}
            />
          </ButtonWrapperStyled>
        </ProviderInfoColumnStyled>
      </ProviderTableRowStyled>
    );
  };

  const displayMoreProviders = () => {
    return (
      <ProviderTableStyled id="provider-table-styled">
        {pcpMembers && displaySubscriberProvider()}
        {pcpMembers?.dependents &&
          pcpMembers.dependents.length > 0 &&
          displayDependentProvider()}
      </ProviderTableStyled>
    );
  };

  return (
    <div data-testid="pcp-plan-details">
      {isAddProvider &&
        !validatePcpEmployee(pcpMembers) &&
        !validatePcpDependents(pcpMembers) && (
          <ProviderWrapperStyled data-testid="providers-warning-message">
            <ProviderBoxStyled>
              <div className="warning-icon">
                <i className="ph ph-lightbulb" />
              </div>
              <Typography fontSize={16}>
                {t('providers.intialTextAddProvider')}
              </Typography>
            </ProviderBoxStyled>
          </ProviderWrapperStyled>
        )}
      {pcpMembers && selected && !isPcpOptional ? (
        <ProviderWrapperStyled>
          {(!isAddProvider ||
            (isMobile &&
              (validatePcpEmployee(pcpMembers) ||
                validatePcpDependents(pcpMembers)))) && (
              <AddedProvidersBoxStyled ref={providerDetailsTable}>
                <ProviderTextWrappperStyled>
                  <div className="svg-wrapper">
                    <Check
                      size={24}
                      color="#2A7527"
                      className="svg-check"
                      style={{ marginRight: '20px' }}
                    />
                  </div>

                  <Typography fontSize={16}>
                    {t('providers.addedProvidersText')}
                  </Typography>
                </ProviderTextWrappperStyled>

                <PsButtonTuner
                  buttonId="view-more-providers-button"
                  label={
                    showMoreProviders
                      ? t('providers.viewLessButtonLabel')
                      : t('providers.viewMoreButtonLabel')
                  }
                  iconRight="caret-down"
                  level="tertiary"
                  className="view-more-providers-button"
                  onClick={handleMoreProvidersClick}
                />
              </AddedProvidersBoxStyled>
            )}

          {(showMoreProviders ||
            (isAddProvider &&
              !isMobile &&
              (validatePcpEmployee(pcpMembers) ||
                validatePcpDependents(pcpMembers)))) &&
            displayMoreProviders()}
          {benefitLookup === 'dental' && isFindAProviderEnabled && (
            <ChoosePCPDrawer
              electPlan={electPlan}
              setPcpElectionData={setPcpElectionData}
              fromPlanDetails={true}
              pcpMembers={pcpMembers}
              selectedDependentIds={selectedDependentIds}
              openChooseProviderDrawer={openChooseProviderDrawer}
              setOpenChooseProviderDrawer={setOpenChooseProviderDrawer}
              setShowPcpDrawer={setShowPcpDrawer}
              plan={plan}
              hideDependentCheckboxWhenChecked={hideDependentCheckboxWhenChecked}
              setHideDependentCheckboxWhenChecked={setHideDependentCheckboxWhenChecked}
              subscribeZipCode={subscribeZipCode}
              selected={selected}
              setPcpDrawerRequired={setPcpDrawerRequired}
              isEsignPlanRequired={isEsignPlanRequired}
            />
          )}
        </ProviderWrapperStyled>
      ) : (!isAddProvider &&
        <ProviderWrapperStyled data-testid="providers-warning-message">
          <ProviderBoxStyled>
            <div className="warning-icon">
              <i className="ph ph-lightbulb" />
            </div>
            <Typography fontSize={16}>{isPcpOptional ?
              t('providers.optionalInitialText') :
              t('providers.mandateInitialText')}</Typography>
          </ProviderBoxStyled>
        </ProviderWrapperStyled>
      )}
    </div>
  );
};
